import { FehlerCode, GurtbreiteType, OrderDocumentStatusEnum, PalletBoxType, PositionActivityType, WerkType } from '../models/order-document.model';
import { UserRole } from '../models/user.model';

/**
 * German display labels for each position activity. Keeps the Positionen table dropdown
 * in sync with the enum, mirroring the ROLE_LABELS pattern in role-access.ts.
 */
export const POSITION_ACTIVITY_LABELS: Record<PositionActivityType, string> = {
  [PositionActivityType.KONFEKTIONIEREN]: 'Konfektionieren',
  [PositionActivityType.GURTEN]: 'Gurten',
  [PositionActivityType.PRUEFEN]: 'Prüfen',
  [PositionActivityType.NACHARBEIT]: 'Nacharbeit',
};

/** Dropdown options for the Tätigkeit select, derived from POSITION_ACTIVITY_LABELS. */
export const POSITION_ACTIVITY_OPTIONS = Object.values(PositionActivityType).map(value => ({
  value,
  label: POSITION_ACTIVITY_LABELS[value],
}));

/** Roles that may add, edit, or delete Positionen entries on an order document. */
export const ORDER_DOCUMENT_POSITION_ROLES: UserRole[] = [UserRole.BUERO, UserRole.FERTIGUNGSMITARBEITER];

/** Roles that may view (but not necessarily manage) the Positionen table in edit mode. */
export const ORDER_DOCUMENT_POSITION_VIEW_ROLES: UserRole[] = [
  ...ORDER_DOCUMENT_POSITION_ROLES,
  UserRole.WARENEINGANG,
  UserRole.ENDKONTROLLE,
];

/** German display labels for the manually-set order document status/workflow stage. */
export const ORDER_DOCUMENT_STATUS_LABELS: Record<OrderDocumentStatusEnum, string> = {
  [OrderDocumentStatusEnum.WARENEINGANG]: 'Wareneingang',
  [OrderDocumentStatusEnum.BUERO]: 'Büro',
  [OrderDocumentStatusEnum.FERTIGUNG]: 'Fertigung',
  [OrderDocumentStatusEnum.ENDKONTROLLE]: 'Endkontrolle',
  [OrderDocumentStatusEnum.ABGESCHLOSSEN]: 'Abgeschlossen',
};

/** Dropdown options for the Status select, derived from ORDER_DOCUMENT_STATUS_LABELS. */
export const ORDER_DOCUMENT_STATUS_OPTIONS = Object.values(OrderDocumentStatusEnum).map(value => ({
  value,
  label: ORDER_DOCUMENT_STATUS_LABELS[value],
}));

/** Workflow order for the Status quick-advance action, in the enum's declared order. */
const ORDER_DOCUMENT_STATUS_SEQUENCE = Object.values(OrderDocumentStatusEnum);

/**
 * Next status in the workflow, or null if already at the last stage. Mirrors
 * DeliveryNoteService.getNextStatus.
 */
export function getNextOrderDocumentStatus(current: OrderDocumentStatusEnum): OrderDocumentStatusEnum | null {
  const index = ORDER_DOCUMENT_STATUS_SEQUENCE.indexOf(current);
  if (index === -1 || index === ORDER_DOCUMENT_STATUS_SEQUENCE.length - 1) return null;
  return ORDER_DOCUMENT_STATUS_SEQUENCE[index + 1];
}

/** CSS classes for the Status badge, mirroring DeliveryNoteService.getStatusClass. */
export const ORDER_DOCUMENT_STATUS_CLASSES: Record<OrderDocumentStatusEnum, string> = {
  [OrderDocumentStatusEnum.WARENEINGANG]: 'bg-yellow-400/15 text-yellow-400',
  [OrderDocumentStatusEnum.BUERO]: 'bg-blue-400/15 text-blue-400',
  [OrderDocumentStatusEnum.FERTIGUNG]: 'bg-purple-400/15 text-purple-400',
  [OrderDocumentStatusEnum.ENDKONTROLLE]: 'bg-orange-400/15 text-orange-400',
  [OrderDocumentStatusEnum.ABGESCHLOSSEN]: 'bg-emerald-400/15 text-emerald-400',
};

/** German display labels for each Fehler (error) code, keyed by the printed shop-floor code. */
export const FEHLER_LABELS: Record<FehlerCode, string> = {
  [FehlerCode.AR]: 'Entgurten / Neugurten',
  [FehlerCode.C]: 'Beschädigte Anschlüsse',
  [FehlerCode.BR]: 'Beschädigte Gehäuse',
  [FehlerCode.Fr]: 'Lücken ausschneiden',
  [FehlerCode.G]: 'Ersatzgurt einsetzen',
  [FehlerCode.H]: 'Splicestelle erneuern',
  [FehlerCode.VR]: 'Vorlauf / Nachlauf',
  [FehlerCode.K]: 'Drähte außerhalb Klebeband',
  [FehlerCode.LR]: 'Polarität richtigstellen',
  [FehlerCode.NRAD]: 'Nacharbeitsscheine Radial',
  [FehlerCode.DN]: 'Deckfolie neu',
  [FehlerCode.LS]: 'Polarität richtigstellen',
  [FehlerCode.F]: 'Fremdkörper entfernen',
  [FehlerCode.BS]: 'Defekte Teile ersetzen',
  [FehlerCode.FS]: 'Lücken',
  [FehlerCode.AS]: 'Entgurten / Neugurten',
  [FehlerCode.DP]: 'Doppelte Teile',
  [FehlerCode.MV]: 'Mittelversiegelung',
  [FehlerCode.BIS]: 'extern BIS',
  [FehlerCode.NSMD]: 'Nacharbeitsscheine',
  [FehlerCode.SF]: 'Stückzahlfehler',
  [FehlerCode.FE]: 'Falsche Etiketten',
  [FehlerCode.XYZ]: 'Sonstiges',
};

/** Dropdown options for the Fehler select, derived from FEHLER_LABELS (one flat searchable list). */
export const FEHLER_OPTIONS = Object.values(FehlerCode).map(value => ({
  value,
  label: FEHLER_LABELS[value],
}));

/** German display labels for the Paletten-/Box-Typ dropdown next to the Paletten-/Boxnummer field. */
export const PALLET_BOX_TYPE_LABELS: Record<PalletBoxType, string> = {
  [PalletBoxType.PALETTE]: 'Palette',
  [PalletBoxType.BOX]: 'Box',
};

/** Dropdown options for the Paletten-/Box-Typ select, derived from PALLET_BOX_TYPE_LABELS. */
export const PALLET_BOX_TYPE_OPTIONS = Object.values(PalletBoxType).map(value => ({
  value,
  label: PALLET_BOX_TYPE_LABELS[value],
}));

/** German display labels for the Werk (plant) an order document is processed in. */
export const WERK_LABELS: Record<WerkType, string> = {
  [WerkType.WERK_1]: 'Werk 1',
  [WerkType.WERK_2]: 'Werk 2',
  [WerkType.WERK_3]: 'Werk 3',
};

/** Dropdown options for the Werk select, derived from WERK_LABELS. */
export const WERK_OPTIONS = Object.values(WerkType).map(value => ({
  value,
  label: WERK_LABELS[value],
}));

/** German display labels for the Gurtbreite (tape/belt width). */
export const GURTBREITE_LABELS: Record<GurtbreiteType, string> = {
  [GurtbreiteType.MM_8]: '8 mm',
  [GurtbreiteType.MM_12]: '12 mm',
  [GurtbreiteType.MM_16]: '16 mm',
  [GurtbreiteType.MM_24]: '24 mm',
  [GurtbreiteType.MM_32]: '32 mm',
  [GurtbreiteType.MM_44]: '44 mm',
  [GurtbreiteType.MM_56]: '56 mm',
  [GurtbreiteType.MM_72]: '72 mm',
  [GurtbreiteType.MM_88]: '88 mm',
  [GurtbreiteType.MM_104]: '104 mm',
  [GurtbreiteType.MIXED]: 'Mixed',
};

/** Dropdown options for the Gurtbreite select, derived from GURTBREITE_LABELS. */
export const GURTBREITE_OPTIONS = Object.values(GurtbreiteType).map(value => ({
  value,
  label: GURTBREITE_LABELS[value],
}));
